/**
 * Operations Tasks Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  fetchTasks,
  fetchTask,
  fetchKanbanTasks,
  fetchTasksStats,
  fetchTasksByProject,
  createTask,
  updateTask,
  deleteTask,
  updateTaskStatus,
  assignTask,
  updateTaskPositions,
  addTaskComment,
  deleteTaskComment,
  fetchTaskAuditLogs,
  fetchQuoteOptions,
  fetchProjectOptions,
  fetchAssigneeOptions,
  fetchTaskAttachments,
  uploadTaskAttachment,
  deleteTaskAttachment,
} from '../api';
import type {
  TasksQueryParams,
  KanbanQueryParams,
  KanbanData,
  CreateTaskInput,
  UpdateTaskInput,
  UpdatePositionsInput,
  TaskStatus,
  TaskListItem,
} from '../types';

// =============================================================================
// Query Key Factory
// =============================================================================

export const tasksKeys = {
  all: ['operations-tasks'] as const,
  lists: () => [...tasksKeys.all, 'list'] as const,
  list: (params: TasksQueryParams) => [...tasksKeys.lists(), params] as const,
  details: () => [...tasksKeys.all, 'detail'] as const,
  detail: (id: string) => [...tasksKeys.details(), id] as const,
  kanban: (params?: KanbanQueryParams) => [...tasksKeys.all, 'kanban', params] as const,
  stats: (params?: { projectId?: string; quoteId?: string; assignedToId?: string }) =>
    [...tasksKeys.all, 'stats', params] as const,
  byProject: (projectId: string, params?: { pageNumber?: number; pageLength?: number; status?: TaskStatus; includeSubtasks?: boolean }) =>
    [...tasksKeys.all, 'by-project', projectId, params] as const,
  byQuote: (quoteId: string, params?: { pageNumber?: number; pageLength?: number; status?: TaskStatus; includeSubtasks?: boolean }) =>
    [...tasksKeys.all, 'by-quote', quoteId, params] as const,
  auditLogs: (taskId: string, params?: { pageNumber?: number; pageLength?: number }) =>
    [...tasksKeys.all, 'audit-logs', taskId, params] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

export function useTasks(params: TasksQueryParams = {}) {
  return useQuery({
    queryKey: tasksKeys.list(params),
    queryFn: () => fetchTasks(params),
  });
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: tasksKeys.detail(id!),
    queryFn: () => fetchTask(id!),
    enabled: !!id,
  });
}

export function useKanbanTasks(params: KanbanQueryParams = {}) {
  return useQuery({
    queryKey: tasksKeys.kanban(params),
    queryFn: () => fetchKanbanTasks(params),
    staleTime: 10000, // 10 seconds - Kanban data changes frequently
  });
}

export function useTasksStats(params: { projectId?: string; quoteId?: string; assignedToId?: string } = {}) {
  return useQuery({
    queryKey: tasksKeys.stats(params),
    queryFn: () => fetchTasksStats(params),
    staleTime: 30000, // 30 seconds
  });
}

export function useTasksByProject(
  projectId: string | undefined,
  params: { pageNumber?: number; pageLength?: number; status?: TaskStatus; includeSubtasks?: boolean } = {}
) {
  return useQuery({
    queryKey: tasksKeys.byProject(projectId!, params),
    queryFn: () => fetchTasksByProject(projectId!, params),
    enabled: !!projectId,
  });
}

export function useTasksByQuote(
  quoteId: string | undefined,
  params: { pageNumber?: number; pageLength?: number; status?: TaskStatus; includeSubtasks?: boolean } = {}
) {
  return useQuery({
    queryKey: tasksKeys.byQuote(quoteId!, params),
    queryFn: () => fetchTasks({ ...params, quoteId: quoteId! }),
    enabled: !!quoteId,
  });
}

export function useTaskAuditLogs(
  taskId: string | undefined,
  params: { pageNumber?: number; pageLength?: number } = {}
) {
  return useQuery({
    queryKey: tasksKeys.auditLogs(taskId!, params),
    queryFn: () => fetchTaskAuditLogs(taskId!, params),
    enabled: !!taskId,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

export function useCreateTask() {
  const queryClient = useQueryClient();

  // Base key for matching all kanban queries regardless of filter params
  const kanbanBaseKey = [...tasksKeys.all, 'kanban'] as const;
  // Base key for matching all by-project queries regardless of params
  const byProjectBaseKey = [...tasksKeys.all, 'by-project'] as const;
  const byQuoteBaseKey = [...tasksKeys.all, 'by-quote'] as const;

  return useMutation({
    mutationFn: (input: CreateTaskInput) => createTask(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
      queryClient.invalidateQueries({ queryKey: kanbanBaseKey });
      queryClient.invalidateQueries({ queryKey: byProjectBaseKey });
      queryClient.invalidateQueries({ queryKey: byQuoteBaseKey });
      queryClient.invalidateQueries({ queryKey: tasksKeys.stats() });
      toast.success('Task created successfully');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to create task';
      toast.error(message);
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();

  // Base key for matching all kanban queries regardless of filter params
  const kanbanBaseKey = [...tasksKeys.all, 'kanban'] as const;
  // Base key for matching all by-project queries regardless of params
  const byProjectBaseKey = [...tasksKeys.all, 'by-project'] as const;
  const byQuoteBaseKey = [...tasksKeys.all, 'by-quote'] as const;

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTaskInput }) => updateTask(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
      queryClient.invalidateQueries({ queryKey: kanbanBaseKey });
      queryClient.invalidateQueries({ queryKey: byProjectBaseKey });
      queryClient.invalidateQueries({ queryKey: byQuoteBaseKey });
      queryClient.invalidateQueries({ queryKey: tasksKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: tasksKeys.stats() });
      toast.success('Task updated successfully');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to update task';
      toast.error(message);
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();

  // Base key for matching all kanban queries regardless of filter params
  const kanbanBaseKey = [...tasksKeys.all, 'kanban'] as const;
  // Base key for matching all by-project queries regardless of params
  const byProjectBaseKey = [...tasksKeys.all, 'by-project'] as const;
  const byQuoteBaseKey = [...tasksKeys.all, 'by-quote'] as const;

  return useMutation({
    mutationFn: (id: string) => deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
      queryClient.invalidateQueries({ queryKey: kanbanBaseKey });
      queryClient.invalidateQueries({ queryKey: byProjectBaseKey });
      queryClient.invalidateQueries({ queryKey: byQuoteBaseKey });
      queryClient.invalidateQueries({ queryKey: tasksKeys.stats() });
      toast.success('Task deleted successfully');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to delete task';
      toast.error(message);
    },
  });
}

export function useUpdateTaskStatus() {
  const queryClient = useQueryClient();

  // Base key for matching all kanban queries regardless of filter params
  const kanbanBaseKey = [...tasksKeys.all, 'kanban'] as const;
  // Base key for matching all by-project queries regardless of params
  const byProjectBaseKey = [...tasksKeys.all, 'by-project'] as const;
  const byQuoteBaseKey = [...tasksKeys.all, 'by-quote'] as const;

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: TaskStatus }) => updateTaskStatus(id, status),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
      queryClient.invalidateQueries({ queryKey: kanbanBaseKey });
      queryClient.invalidateQueries({ queryKey: byProjectBaseKey });
      queryClient.invalidateQueries({ queryKey: byQuoteBaseKey });
      queryClient.invalidateQueries({ queryKey: tasksKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: tasksKeys.stats() });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to update task status';
      toast.error(message);
    },
  });
}

export function useAssignTask() {
  const queryClient = useQueryClient();

  // Base key for matching all kanban queries regardless of filter params
  const kanbanBaseKey = [...tasksKeys.all, 'kanban'] as const;
  // Base key for matching all by-project queries regardless of params
  const byProjectBaseKey = [...tasksKeys.all, 'by-project'] as const;
  const byQuoteBaseKey = [...tasksKeys.all, 'by-quote'] as const;

  return useMutation({
    mutationFn: ({
      id,
      assignee,
    }: {
      id: string;
      assignee: { assigned_to_id?: string; assigned_to_name?: string; assigned_to_email?: string };
    }) => assignTask(id, assignee),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
      queryClient.invalidateQueries({ queryKey: kanbanBaseKey });
      queryClient.invalidateQueries({ queryKey: byProjectBaseKey });
      queryClient.invalidateQueries({ queryKey: byQuoteBaseKey });
      queryClient.invalidateQueries({ queryKey: tasksKeys.detail(data.id) });
      toast.success('Task assigned successfully');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to assign task';
      toast.error(message);
    },
  });
}

/**
 * Hook for updating task positions with optimistic updates
 * Used for Kanban drag-and-drop
 */
export function useUpdateTaskPositions() {
  const queryClient = useQueryClient();

  // Base key for matching all kanban queries regardless of filter params
  const kanbanBaseKey = [...tasksKeys.all, 'kanban'] as const;
  // Base key for matching all by-project queries regardless of params
  const byProjectBaseKey = [...tasksKeys.all, 'by-project'] as const;
  const byQuoteBaseKey = [...tasksKeys.all, 'by-quote'] as const;

  return useMutation({
    mutationFn: (input: UpdatePositionsInput) => updateTaskPositions(input),
    // Optimistic update for smooth drag-and-drop
    onMutate: async (input) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: kanbanBaseKey });

      // Snapshot the previous value
      const previousKanban = queryClient.getQueriesData({ queryKey: kanbanBaseKey });

      // Optimistically update the kanban data
      queryClient.setQueriesData<KanbanData>(
        { queryKey: kanbanBaseKey },
        (old) => {
          if (!old) return old;

          const newColumns = old.columns.map((column) => ({
            ...column,
            tasks: [...column.tasks],
          }));

          // Apply position/status updates
          for (const update of input.updates) {
            // Find and remove task from current column
            let movedTask: TaskListItem | undefined;
            for (const column of newColumns) {
              const taskIndex = column.tasks.findIndex((t) => t.id === update.id);
              if (taskIndex !== -1) {
                [movedTask] = column.tasks.splice(taskIndex, 1);
                break;
              }
            }

            if (movedTask) {
              // Update task properties
              movedTask.position = update.position;
              if (update.status) {
                movedTask.status = update.status;
              }

              // Find target column and insert at correct position
              const targetStatus = update.status || movedTask.status;
              const targetColumn = newColumns.find((c) => c.status === targetStatus);
              if (targetColumn) {
                // Insert at position
                const insertIndex = targetColumn.tasks.findIndex(
                  (t) => t.position > update.position
                );
                if (insertIndex === -1) {
                  targetColumn.tasks.push(movedTask);
                } else {
                  targetColumn.tasks.splice(insertIndex, 0, movedTask);
                }
              }
            }
          }

          return { ...old, columns: newColumns };
        }
      );

      return { previousKanban };
    },
    // On error, rollback to previous state
    onError: (error, _variables, context) => {
      if (context?.previousKanban) {
        for (const [queryKey, data] of context.previousKanban) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      const message = error instanceof Error ? error.message : 'Failed to update task position';
      toast.error(message);
    },
    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: kanbanBaseKey });
      queryClient.invalidateQueries({ queryKey: byProjectBaseKey });
      queryClient.invalidateQueries({ queryKey: byQuoteBaseKey });
      queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
    },
  });
}

export function useAddTaskComment() {
  const queryClient = useQueryClient();

  // Base key for matching all kanban queries regardless of filter params
  const kanbanBaseKey = [...tasksKeys.all, 'kanban'] as const;
  const byQuoteBaseKey = [...tasksKeys.all, 'by-quote'] as const;

  return useMutation({
    mutationFn: ({ taskId, content }: { taskId: string; content: string }) =>
      addTaskComment(taskId, content),
    onSuccess: (_data, variables) => {
      // Invalidate detail to show new comment
      queryClient.invalidateQueries({ queryKey: tasksKeys.detail(variables.taskId) });
      // Also invalidate lists to update comment counts in list views
      queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
      queryClient.invalidateQueries({ queryKey: kanbanBaseKey });
      queryClient.invalidateQueries({ queryKey: byQuoteBaseKey });
      toast.success('Comment added successfully');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to add comment';
      toast.error(message);
    },
  });
}

export function useDeleteTaskComment() {
  const queryClient = useQueryClient();

  // Base key for matching all kanban queries regardless of filter params
  const kanbanBaseKey = [...tasksKeys.all, 'kanban'] as const;
  const byQuoteBaseKey = [...tasksKeys.all, 'by-quote'] as const;

  return useMutation({
    mutationFn: ({ taskId, commentId }: { taskId: string; commentId: string }) =>
      deleteTaskComment(taskId, commentId),
    onSuccess: (_data, variables) => {
      // Invalidate detail to remove deleted comment
      queryClient.invalidateQueries({ queryKey: tasksKeys.detail(variables.taskId) });
      // Also invalidate lists to update comment counts in list views
      queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
      queryClient.invalidateQueries({ queryKey: kanbanBaseKey });
      queryClient.invalidateQueries({ queryKey: byQuoteBaseKey });
      toast.success('Comment deleted successfully');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to delete comment';
      toast.error(message);
    },
  });
}

// =============================================================================
// Related Data Hooks: Quotes, Projects & Assignees
// =============================================================================

export const relatedDataKeys = {
  quoteOptions: ['task-quote-options'] as const,
  projectOptions: ['task-project-options'] as const,
  assigneeOptions: ['task-assignee-options'] as const,
};

/**
 * Hook to fetch quote options for direct task linking
 */
export function useQuoteOptions() {
  return useQuery({
    queryKey: relatedDataKeys.quoteOptions,
    queryFn: fetchQuoteOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch project options for task linking
 */
export function useProjectOptions() {
  return useQuery({
    queryKey: relatedDataKeys.projectOptions,
    queryFn: fetchProjectOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch assignee options for task assignment
 */
export function useAssigneeOptions() {
  return useQuery({
    queryKey: relatedDataKeys.assigneeOptions,
    queryFn: fetchAssigneeOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// Attachment Hooks
// =============================================================================

export const attachmentKeys = {
  all: (taskId: string) => ['task-attachments', taskId] as const,
};

/**
 * Hook to fetch attachments for a task
 */
export function useTaskAttachments(taskId: string | undefined) {
  return useQuery({
    queryKey: attachmentKeys.all(taskId!),
    queryFn: () => fetchTaskAttachments(taskId!),
    enabled: !!taskId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to upload a task attachment
 */
export function useUploadTaskAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, file }: { taskId: string; file: File }) =>
      uploadTaskAttachment(taskId, file),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: attachmentKeys.all(variables.taskId) });
      queryClient.invalidateQueries({ queryKey: tasksKeys.detail(variables.taskId) });
      toast.success('Attachment uploaded successfully');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to upload attachment';
      toast.error(message);
    },
  });
}

/**
 * Hook to delete a task attachment
 */
export function useDeleteTaskAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, attachmentId }: { taskId: string; attachmentId: string }) =>
      deleteTaskAttachment(taskId, attachmentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: attachmentKeys.all(variables.taskId) });
      toast.success('Attachment deleted successfully');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to delete attachment';
      toast.error(message);
    },
  });
}
